import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserCard from 'flarum/forum/components/UserCard';
import { cssUrl, clampFocus, clampZoom } from '../common/util';
import prefetchImage from './prefetch';

/**
 * Paints the cover (with its focal point) onto every UserCard surface:
 *
 * - the user page hero,
 * - post hover cards      (className: UserCard--popover)   [setting-gated]
 * - fof/user-directory    (className: UserCard--directory) [setting-gated]
 *
 * fof/user-directory's cards subclass core's UserCard, so no extra
 * integration code is needed — the className tells the contexts apart.
 *
 * The image itself is painted by an injected .CoverStudio-cover child with
 * its own overflow clipping. The card must NOT be clipped (overflow: hidden
 * on the card would cut off dropdown menus inside the hero), but the zoom
 * transform makes the image layers extend past the card bounds — hence the
 * dedicated, clipped wrapper.
 */
export default function applyCoverToUserCards() {
  extend(UserCard.prototype, 'view', function (vnode) {
    var _user$coverUrl2, _user$coverFocusX, _user$coverFocusX2, _user$coverFocusY, _user$coverFocusY2, _user$coverZoom, _user$coverZoom2;
    const user = this.attrs.user;
    if (!user || !(vnode != null && vnode.attrs)) return;
    const context = String(this.attrs.className || '');
    const isPopover = context.includes('UserCard--popover');
    const isDirectory = context.includes('UserCard--directory');
    const isHero = !isPopover && !isDirectory;
    if (isPopover && !app.forum.attribute('tryhackx-cover-studio.show_on_popover')) return;
    if (isDirectory && !app.forum.attribute('tryhackx-cover-studio.show_on_directory')) return;

    // On the profile hero of an editable user, quietly warm the cache for the
    // editor source images so the position editors open without waiting
    // behind the page's post images.
    if (isHero) {
      var _user$canSetCover, _user$canSetAvatarFoc;
      if ((_user$canSetCover = user.canSetCover) != null && _user$canSetCover.call(user)) {
        var _user$coverThumbUrl, _user$coverUrl;
        prefetchImage(((_user$coverThumbUrl = user.coverThumbUrl) == null ? void 0 : _user$coverThumbUrl.call(user)) || ((_user$coverUrl = user.coverUrl) == null ? void 0 : _user$coverUrl.call(user)));
      }
      if ((_user$canSetAvatarFoc = user.canSetAvatarFocus) != null && _user$canSetAvatarFoc.call(user) && app.forum.attribute('tryhackx-cover-studio.avatar_focus')) {
        var _user$avatarOriginalU;
        prefetchImage((_user$avatarOriginalU = user.avatarOriginalUrl) == null ? void 0 : _user$avatarOriginalU.call(user));
      }
    }
    let url = (_user$coverUrl2 = user.coverUrl) == null ? void 0 : _user$coverUrl2.call(user);
    let x = clampFocus(Number((_user$coverFocusX = (_user$coverFocusX2 = user.coverFocusX) == null ? void 0 : _user$coverFocusX2.call(user)) != null ? _user$coverFocusX : 50));
    let y = clampFocus(Number((_user$coverFocusY = (_user$coverFocusY2 = user.coverFocusY) == null ? void 0 : _user$coverFocusY2.call(user)) != null ? _user$coverFocusY : 50));
    let zoom = clampZoom(Number((_user$coverZoom = (_user$coverZoom2 = user.coverZoom) == null ? void 0 : _user$coverZoom2.call(user)) != null ? _user$coverZoom : 1));
    let isDefault = false;
    if (url) {
      var _user$coverThumbUrl2;
      // Smaller surfaces get the lightweight thumbnail generated by fof/upload.
      if ((isPopover || isDirectory) && (_user$coverThumbUrl2 = user.coverThumbUrl) != null && _user$coverThumbUrl2.call(user)) {
        url = user.coverThumbUrl();
      }
    } else {
      var _app$forum$attribute, _app$forum$attribute2, _app$forum$attribute3;
      const fallback = app.forum.attribute('tryhackx-cover-studio.default_cover_url');
      if (!fallback) return;
      url = fallback;
      x = clampFocus(Number((_app$forum$attribute = app.forum.attribute('tryhackx-cover-studio.default_cover_focus_x')) != null ? _app$forum$attribute : 50));
      y = clampFocus(Number((_app$forum$attribute2 = app.forum.attribute('tryhackx-cover-studio.default_cover_focus_y')) != null ? _app$forum$attribute2 : 50));
      zoom = clampZoom(Number((_app$forum$attribute3 = app.forum.attribute('tryhackx-cover-studio.default_cover_zoom')) != null ? _app$forum$attribute3 : 1));
      isDefault = true;
    }
    const attrs = vnode.attrs;
    const style = attrs.style && typeof attrs.style === 'object' ? attrs.style : attrs.style = {};
    style['--cs-cover'] = cssUrl(url);
    style['--cs-x'] = "".concat(x, "%");
    style['--cs-y'] = "".concat(y, "%");
    style['--cs-zoom'] = "".concat(zoom);
    attrs.className = "".concat(attrs.className || '', " CoverStudio-hasCover") + (zoom < 1 ? ' CoverStudio-zoomOut' : '') + (isDefault ? ' CoverStudio-defaultCover' : '');

    // Clipped image layer, inserted below the card content. The overlay child
    // spans the FULL card height (unlike .darkenBackground, which hugs the
    // bottom-pinned content on tall heroes), so the readability gradient runs
    // smoothly from the top edge instead of starting abruptly mid-cover.
    if (Array.isArray(vnode.children)) {
      vnode.children.unshift(m('div', {
        className: 'CoverStudio-cover',
        'aria-hidden': 'true'
      }, m('div', {
        className: 'CoverStudio-cover-overlay'
      })));
    }
  });
}